import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { APIResponse } from '../common/common.model';
import {
    mapBoolean,
    mapDelete,
    mapList,
    mapRetrieve,
    mapUpdate,
} from '../common/rest.operators';

import { UserSearchCriteria, UserSearchResult } from './search-user.model';
import {
    ExtUserName,
    SecurityQuestion,
    SecurityQuestionModelTransformer,
    User,
    UserCreateModelTransformer,
    UserEmployeeType,
    UserEmployeeTypeModelSetTransformer,
    UserEmployeeTypeModelTransformer,
    UserHierarchy,
    UserHierarchyModelSetTransformer,
    UserHierarchyModelTransformer,
    UserModelTransformer,
    UserRole,
    UserRoleModelSetTransformer,
    UserRoleModelTransformer,
    UserSecurityGroupAssociation,
    UserSecurityGroupAssociationModelSetTransformer,
    UserSecurityGroupAssociationModelTransformer,
    UserSecurityInfo,
    UserSecurityInfoModelTransformer,
    UserSite,
    UserSiteModelSetTransformer,
    UserSiteModelTransformer,
    UserSuperGroup,
    UserSuperGroupModelSetTransformer,
    UserSuperGroupModelTransformer,
    UserSystemId,
    UserSystemIdModelSetTransformer,
    UserSystemIdModelTransformer,
} from './user.model';

@Injectable()
export class UserService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    create(model: User): Observable<User> {
        const body = new UserCreateModelTransformer().reverseTransform(model);

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile`,
                body,
            )
            .pipe(
                mapUpdate(new UserModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    retrieve(userId?: number): Observable<User> {
        let params = new HttpParams();
        if (userId != null) {
            params = params.set('userId', userId.toString());
        }
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile`, {
                params,
            })
            .pipe(
                mapRetrieve(new UserModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UserProfile failed');
                }),
            );
    }

    update(model: User): Observable<boolean> {
        const body = new UserModelTransformer().reverseTransform(model);

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    list(searchParams: UserSearchCriteria): Observable<UserSearchResult[]> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile/SearchUsers`,
                searchParams,
            )
            .pipe(
                mapList(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UserProfile/SearchUsers failed');
                }),
            );
    }

    updatePhoto(image: File, userId: number): Observable<boolean> {
        const body = new FormData();
        body.append('image', image);
        body.append('userId', userId.toString());

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersAvatar`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    retrievePhoto(userId: number): Observable<string> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersAvatar`, {
                params,
            })
            .pipe(
                mapRetrieve(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersAvatar failed');
                }),
            );
    }

    deletePhoto(userId: number): Observable<boolean> {
        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersAvatar/${userId}`,
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UsersAvatar failed');
                }),
            );
    }

    retrieveSecurityQuestion(username: string): Observable<SecurityQuestion> {
        const params = new HttpParams().set('username', username);

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/account/GetSecurityQuestion`,
                { params },
            )
            .pipe(
                mapRetrieve(new SecurityQuestionModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    // FIXME explicit way of knowing user has no security question
                    if (error.message === 'null') {
                        throw error;
                    } else {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        return of({} as SecurityQuestion);
                    }
                }),
            );
    }

    updateSecurityQuestion(model: SecurityQuestion): Observable<boolean> {
        const body = new SecurityQuestionModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER}/account/UpdateSecurityQuestion`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    checkIfUsernameExists(
        username: string,
        exclude: number[],
    ): Observable<APIResponse> {
        let params = new HttpParams().set('username', username);
        if (exclude) {
            // eslint-disable-next-line no-return-assign
            exclude
                .filter((value) => value != null)
                .forEach(
                    (value) =>
                        (params = params.append('exclude', value.toString())),
                );
        }

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/account/IsUserNameUnique`,
                { params },
            )
            .pipe(
                map((r: APIResponse) => {
                    if (!r.isSuccess) {
                        throw new Error(r.message);
                    }
                    return r;
                }),
                catchError((error) => {
                    console.error(error);
                    throw new Error('IsUserNameUnique failed');
                }),
            );
    }

    updateUsername(id: number, username: string): Observable<boolean> {
        const body: ExtUserName = { newUserName: username, userId: id };

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER}/account/UserNameUpdate`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    checkIfEmailExists(
        email: string,
        exclude: number[],
    ): Observable<APIResponse> {
        let params = new HttpParams().set('email', email);
        if (exclude) {
            // eslint-disable-next-line no-return-assign
            exclude
                .filter((value) => value != null)
                .forEach(
                    (value) =>
                        (params = params.append('exclude', value.toString())),
                );
        }

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/account/IsEmailUnique`,
                { params },
            )
            .pipe(
                map((r: APIResponse) => {
                    if (!r.isSuccess) {
                        throw new Error(r.message);
                    }
                    return r;
                }),
                catchError((error) => {
                    console.error(error);
                    throw new Error('IsEmailUnique failed');
                }),
            );
    }

    createSecurityInfo(model: UserSecurityInfo): Observable<boolean> {
        const body = new UserSecurityInfoModelTransformer(
            true,
        ).reverseTransform(model);

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER}/account/AddSecurityUser`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    retrieveSecurityInfo(id: number): Observable<UserSecurityInfo> {
        const params = new HttpParams().set('userId', id.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/account/GetUserSecurityInfo`,
                { params },
            )
            .pipe(
                mapRetrieve(new UserSecurityInfoModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetUserSecurityInfo failed');
                }),
            );
    }

    updateSecurityInfo(model: UserSecurityInfo): Observable<boolean> {
        const body = new UserSecurityInfoModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER}/account/UpdateSecurityInfo`,
                body,
            )
            .pipe(
                mapUpdate(),
                map(() => true),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    createSuperGroup(model: UserSuperGroup): Observable<UserSuperGroup> {
        const body = new UserSuperGroupModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSuperGroups`,
                body,
            )
            .pipe(
                mapUpdate(new UserSuperGroupModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateSuperGroup(model: UserSuperGroup): Observable<boolean> {
        const body = new UserSuperGroupModelTransformer().reverseTransform(
            model,
        );
        const params = new HttpParams().set('id', model.id.toString());

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSuperGroups`,
                body,
                { params, observe: 'body' },
            )
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteSuperGroup(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSuperGroups`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UsersSuperGroups failed');
                }),
            );
    }

    listSuperGroups(userId: number): Observable<UserSuperGroup[]> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSuperGroups`,
                { params },
            )
            .pipe(
                mapList(new UserSuperGroupModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersSuperGroups failed');
                }),
            );
    }

    createHierarchy(
        model: UserHierarchy,
        which: 'managers' | 'subordinates',
    ): Observable<UserHierarchy> {
        const body = new UserHierarchyModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersHierarchy`,
                body,
            )
            .pipe(
                mapUpdate(new UserHierarchyModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateHierarchy(
        model: UserHierarchy,
        which: 'managers' | 'subordinates',
    ): Observable<boolean> {
        const body = new UserHierarchyModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersHierarchy`,
                body,
                { observe: 'body' },
            )
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteHierarchy(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersHierarchy`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UserHierarchy failed');
                }),
            );
    }

    listHierarchy(
        which: 'managers' | 'subordinates',
        userId: number,
    ): Observable<UserHierarchy[]> {
        const params = new HttpParams().set(
            which === 'managers' ? 'userId' : 'managerId',
            userId.toString(),
        );

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersHierarchy/${which}`,
                { params },
            )
            .pipe(
                mapList(new UserHierarchyModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersHierarchy failed');
                }),
            );
    }

    createSystemId(model: UserSystemId): Observable<UserSystemId> {
        const body = new UserSystemIdModelTransformer().reverseTransform(model);

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSystemIds`,
                body,
            )
            .pipe(
                mapUpdate(new UserSystemIdModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateSystemId(model: UserSystemId): Observable<boolean> {
        const body = new UserSystemIdModelTransformer().reverseTransform(model);
        const params = new HttpParams().set('id', model.id.toString());

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSystemIds`,
                body,
                { params, observe: 'body' },
            )
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteSystemId(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSystemIds`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UserSystemId failed');
                }),
            );
    }

    listSystemIds(userId: number): Observable<UserSystemId[]> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSystemIds`,
                { params },
            )
            .pipe(
                mapList(new UserSystemIdModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersSystemIds failed');
                }),
            );
    }

    createSecurityGroupAssociation(
        model: UserSecurityGroupAssociation,
    ): Observable<UserSecurityGroupAssociation> {
        const body =
            new UserSecurityGroupAssociationModelTransformer().reverseTransform(
                model,
            );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER}/SecurityGroupAssociation`,
                body,
            )
            .pipe(
                mapUpdate(new UserSecurityGroupAssociationModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteSecurityGroupAssociation(id: number): Observable<boolean> {
        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER}/SecurityGroupAssociation/${id}`,
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete SecurityGroupAssociation failed');
                }),
            );
    }

    listSecurityGroupAssociations(
        userId: number,
    ): Observable<UserSecurityGroupAssociation[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER}/SecurityGroupAssociation/user/${userId}`,
            )
            .pipe(
                mapList(new UserSecurityGroupAssociationModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('SecurityGroupAssociation failed');
                }),
            );
    }

    createRole(model: UserRole): Observable<UserRole> {
        const body = new UserRoleModelTransformer().reverseTransform(model);

        return this._http
            .post(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/role`, body)
            .pipe(
                mapUpdate(new UserRoleModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateRole(model: UserRole): Observable<boolean> {
        const body = new UserRoleModelTransformer().reverseTransform(model);
        const params = new HttpParams().set('userRoleId', model.id.toString());

        return this._http
            .put(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/role`, body, {
                params,
                observe: 'body',
            })
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteRole(id: number): Observable<boolean> {
        const params = new HttpParams().set('userRoleId', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/role/DeleteUserRole`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('DeleteUserRole failed');
                }),
            );
    }

    listRoles(userId: number): Observable<UserRole[]> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/role/GetUserRoles`,
                { params },
            )
            .pipe(
                mapList(new UserRoleModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetUserRoles failed');
                }),
            );
    }

    createEmployeeType(model: UserEmployeeType): Observable<UserEmployeeType> {
        const body = new UserEmployeeTypeModelTransformer().reverseTransform(
            model,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersEmployeeTypes`,
                body,
            )
            .pipe(
                mapUpdate(new UserEmployeeTypeModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateEmployeeType(model: UserEmployeeType): Observable<boolean> {
        const body = new UserEmployeeTypeModelTransformer().reverseTransform(
            model,
        );
        const params = new HttpParams().set('id', model.id.toString());

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersEmployeeTypes`,
                body,
                { params, observe: 'body' },
            )
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteEmployeeType(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersEmployeeTypes`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UsersEmployeeTypes failed');
                }),
            );
    }

    listEmployeeTypes(userId: number): Observable<UserEmployeeType[]> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersEmployeeTypes`,
                { params },
            )
            .pipe(
                mapList(new UserEmployeeTypeModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersEmployeeTypes failed');
                }),
            );
    }

    createSite(model: UserSite): Observable<UserSite> {
        const body = new UserSiteModelTransformer().reverseTransform(model);

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSites`,
                body,
            )
            .pipe(
                mapUpdate(new UserSiteModelTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    updateSite(model: UserSite): Observable<boolean> {
        const body = new UserSiteModelTransformer().reverseTransform(model);
        const params = new HttpParams().set('id', model.id.toString());

        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSites`,
                body,
                { params, observe: 'body' },
            )
            .pipe(
                mapBoolean(),
                catchError((error) => {
                    console.error(error);
                    throw error;
                }),
            );
    }

    deleteSite(id: number): Observable<boolean> {
        const params = new HttpParams().set('id', id.toString());

        return this._http
            .delete(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSites`,
                { params },
            )
            .pipe(
                mapDelete(),
                catchError((error) => {
                    console.error(error);
                    throw new Error('delete UsersSites failed');
                }),
            );
    }

    listSites(userId: number): Observable<UserSite[]> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UsersSites`, {
                params,
            })
            .pipe(
                mapList(new UserSiteModelSetTransformer()),
                catchError((error) => {
                    console.error(error);
                    throw new Error('UsersSites failed');
                }),
            );
    }
}
